import axios from 'axios';
import { supabase } from '../lib/supabaseClient';

// Lấy API key từ biến môi trường
let OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY || process.env.OPENROUTER_API_KEY || '';

// Địa chỉ proxy server
const PROXY_URL = import.meta.env.VITE_AI_PROXY_URL || 'http://localhost:3005/api/ai-proxy';

// Model mặc định và backup models
const DEFAULT_MODEL = import.meta.env.VITE_DEFAULT_AI_MODEL || process.env.DEFAULT_AI_MODEL || 'google/gemini-2.0-flash-lite-001';
const BACKUP_MODELS = [
  'anthropic/claude-3-sonnet',
  'anthropic/claude-3-haiku',
  'anthropic/claude-3-opus',
  'google/gemini-pro'
];

// Kiểm tra debug mode
const DEBUG_MODE = import.meta.env.VITE_DEBUG === "true" || process.env.DEBUG === "true";

/**
 * Cập nhật API key
 * @param {string} apiKey - API key mới
 */
export function setApiKey(apiKey) {
  if (apiKey && typeof apiKey === 'string') {
    OPENROUTER_API_KEY = apiKey;
    console.log('Đã cập nhật OpenRouter API key');
  }
}

/**
 * Lấy API key hiện tại
 * @returns {string} - API key hiện tại
 */
export function getApiKey() {
  return OPENROUTER_API_KEY;
}

/**
 * Hàm chuẩn hóa ID model để đảm bảo không có lỗi khi gọi API
 * @param {string} modelId - ID model cần chuẩn hóa
 * @returns {string} - ID model đã chuẩn hóa
 */
const normalizeModelId = (modelId) => {
  if (!modelId) return DEFAULT_MODEL;
  
  // Loại bỏ phần ngày tháng ở cuối nếu có (ví dụ: -20240229)
  return modelId.replace(/-\d{8}$/, '');
};

// Hàm lấy hoặc tạo user ID
const getUserId = () => {
  // Kiểm tra xem đã có user ID trong localStorage chưa
  const storedUserId = typeof window !== 'undefined' ? localStorage.getItem('ai_user_id') : null;
  
  if (storedUserId) {
    return storedUserId;
  }
  
  // Nếu chưa có, tạo mới và lưu vào localStorage
  const newUserId = "user-" + Math.random().toString(36).substring(2, 15);
  
  if (typeof window !== 'undefined') {
    localStorage.setItem('ai_user_id', newUserId);
  }
  
  return newUserId;
};

/**
 * Lấy model ID từ localStorage hoặc dùng mặc định
 * @returns {Promise<string>} - Model ID
 */
const getModelIdFromStorage = async () => {
  // Thử lấy từ localStorage trước
  if (typeof window !== 'undefined') {
    const storedModel = localStorage.getItem('selected_model');
    if (storedModel) return storedModel;
  }
  return DEFAULT_MODEL;
};

/**
 * Lấy system prompt cho chat
 * @param {string} expertId - ID hoặc field của chuyên gia AI
 * @param {string} lessonName - Tên bài học
 * @returns {Promise<string>} - System prompt
 */
const getSystemPrompt = async (expertId, lessonName) => {
  // System prompt mặc định
  let prompt = "Bạn là trợ lý AI thông minh và hữu ích. Hãy trả lời câu hỏi của người dùng một cách chính xác và hữu ích.";
  
  // Thêm thông tin bài học nếu có
  if (lessonName) {
    prompt += `\n\nBạn đang hỗ trợ người dùng trong bài học "${lessonName}". Hãy giải thích các khái niệm liên quan đến bài học này.`;
  }
  
  // Đảm bảo expertId luôn có giá trị, mặc định là "general"
  expertId = expertId || "general";
  
  try {
    console.log(`Tìm expert với ID/field: ${expertId}`);
    
    // Truy vấn với field trước
    let { data: expertDataByField, error: expertErrorByField } = await supabase
      .from('experts')
      .select('*')
      .eq('field', expertId)
      .limit(1);
      
    // Nếu không tìm thấy bằng field, thử tìm bằng id
    if (!expertDataByField || expertDataByField.length === 0) {
      const { data: expertDataById, error: expertErrorById } = await supabase
        .from('experts')
        .select('*')
        .eq('id', expertId)
        .limit(1);
        
      if (expertErrorById) {
        console.warn(`Lỗi khi truy vấn expert với ID ${expertId}:`, expertErrorById);
      } else if (expertDataById && expertDataById.length > 0 && expertDataById[0].system_prompt) {
        console.log(`Đã tìm thấy expert với ID ${expertId}`);
        prompt = expertDataById[0].system_prompt;
        return prompt;
      }
    } else if (expertErrorByField) {
      console.warn(`Lỗi khi truy vấn expert với field ${expertId}:`, expertErrorByField);
    } else if (expertDataByField && expertDataByField.length > 0 && expertDataByField[0].system_prompt) {
      console.log(`Đã tìm thấy expert với field ${expertId}`);
      prompt = expertDataByField[0].system_prompt;
      return prompt;
    }
    
    // Nếu không tìm thấy với field/ID hoặc expertId là "general", tìm expert mặc định
    console.log("Tìm expert mặc định...");
    const { data: defaultExpertData, error: defaultError } = await supabase
      .from('experts')
      .select('*')
      .eq('is_default', true)
      .limit(1);
    
    if (defaultError) {
      console.warn('Lỗi khi tìm expert mặc định:', defaultError);
    } else if (defaultExpertData && defaultExpertData.length > 0 && defaultExpertData[0].system_prompt) {
      console.log('Đã tìm thấy expert mặc định');
      prompt = defaultExpertData[0].system_prompt;
    }
  } catch (error) {
    console.warn(`Không thể lấy thông tin chuyên gia ${expertId}:`, error);
  }
  
  return prompt;
};

/**
 * Lấy lịch sử chat từ storage
 * @param {string} conversationId - ID cuộc hội thoại
 * @returns {Promise<Array>} - Lịch sử chat
 */
const getChatHistoryFromStorage = async (conversationId) => {
  if (!conversationId || typeof window === 'undefined') return [];
  
  try {
    const key = `chat_history_${conversationId}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Lỗi khi lấy lịch sử chat:', error);
  }
  
  return [];
};

/**
 * Lưu lịch sử chat vào storage
 * @param {string} conversationId - ID cuộc hội thoại
 * @param {Object} userMessage - Tin nhắn người dùng
 * @param {Object} aiMessage - Tin nhắn AI
 */
const saveChatHistoryToStorage = async (conversationId, userMessage, aiMessage) => {
  if (!conversationId || typeof window === 'undefined') return;
  
  try {
    const key = `chat_history_${conversationId}`;
    const history = await getChatHistoryFromStorage(conversationId);
    
    // Thêm tin nhắn mới
    history.push(userMessage);
    history.push(aiMessage);
    
    // Giới hạn lịch sử để không quá dài
    if (history.length > 100) {
      history.splice(0, history.length - 100);
    }
    
    localStorage.setItem(key, JSON.stringify(history));
  } catch (error) {
    console.error('Lỗi khi lưu lịch sử chat:', error);
  }
};

/**
 * Cập nhật số credits đã sử dụng cho AI
 * @param {number} amount - Số credits cần cập nhật (âm)
 * @returns {Promise<boolean>} - Kết quả cập nhật
 */
export async function updateAICreditsUsed(amount) {
  if (!amount || amount >= 0) return false; // Chỉ chấp nhận giá trị âm
  
  try {
    const userId = getUserId();
    if (!userId) return false;
    
    // Kiểm tra user có đăng nhập không trước khi gọi API
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      console.warn('Người dùng chưa đăng nhập, không thể cập nhật credits');
      return false;
    }
    
    try {
      // Gọi API để cập nhật credits
      const response = await fetch('/api/rewards/points/deduct-for-ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          amount: Math.abs(amount), // Chuyển thành số dương
          userId: session.user.id
        })
      });
      
      if (response.ok) {
        console.log(`Đã cập nhật ${amount} credits cho user ${userId}`);
        return true;
      } else {
        // Log lỗi và xử lý fallback nếu cần
        console.warn(`API trả về lỗi: ${response.status} ${response.statusText}`);
        
        // Nếu API trả về lỗi 404, thử cập nhật trực tiếp vào database
        if (response.status === 404) {
          return await updateCreditsDirectly(session.user.id, amount);
        }
      }
    } catch (fetchError) {
      console.error('Lỗi khi gọi API cập nhật credits:', fetchError);
      // Nếu lỗi mạng hoặc lỗi khác, thử cập nhật trực tiếp vào database
      return await updateCreditsDirectly(session.user.id, amount);
    }
    
    return false;
  } catch (error) {
    console.error('Lỗi khi cập nhật AI credits:', error);
    return false;
  }
}

/**
 * Cập nhật credits trực tiếp vào database khi API không khả dụng
 * @param {string} userId - ID người dùng
 * @param {number} amount - Số credits cần cập nhật (âm)
 * @returns {Promise<boolean>} - Kết quả cập nhật
 */
async function updateCreditsDirectly(userId, amount) {
  try {
    console.warn('API không tồn tại hoặc lỗi, thử cập nhật credits trực tiếp vào database');
    
    // Cập nhật trực tiếp vào database
    const { error } = await supabase
      .from('users')
      .update({
        ai_credits: supabase.sql('ai_credits + ' + amount),
        ai_credits_used: supabase.sql('ai_credits_used + ' + Math.abs(amount))
      })
      .eq('id', userId);
    
    if (error) {
      console.error('Lỗi khi cập nhật credits trực tiếp:', error);
      return false;
    }
    
    console.log('Đã cập nhật credits trực tiếp vào database');
    return true;
  } catch (error) {
    console.error('Lỗi khi cập nhật credits trực tiếp:', error);
    return false;
  }
}

/**
 * Log thông tin model được sử dụng để debug
 * @param {string} model - Model ID
 * @param {string} context - Context sử dụng model
 */
function logModelUsage(model, context = 'Default') {
  console.log(`[AI Model Debug] ${context}: Sử dụng model ${model}`);
  console.log(`[AI Model Debug] Biến môi trường: VITE_DEFAULT_AI_MODEL=${import.meta.env.VITE_DEFAULT_AI_MODEL}, DEFAULT_AI_MODEL=${process.env.DEFAULT_AI_MODEL}`);
  console.log(`[AI Model Debug] DEFAULT_MODEL được cấu hình: ${DEFAULT_MODEL}`);
}

/**
 * Gửi request đến OpenRouter API qua proxy server và nhận phản hồi
 * @param {string} prompt - Nội dung prompt gửi đến AI
 * @param {Object} options - Các tùy chọn bổ sung
 * @returns {Promise<string>} - Nội dung phản hồi từ AI
 */
export async function handleOpenRouterRequest(prompt, options = {}) {
  try {
    // Kiểm tra prompt có giá trị không
    if (!prompt || typeof prompt !== 'string') {
      console.error('Lỗi: prompt không hợp lệ trong handleOpenRouterRequest', { prompt });
      throw new Error('Prompt không hợp lệ hoặc trống');
    }
    
    // Lấy user ID để theo dõi sử dụng
    const userId = options.user_id || getUserId();
    
    // Lấy model từ options hoặc sử dụng model mặc định
    let model = options.model || DEFAULT_MODEL;
    
    // Kiểm tra model có giá trị
    if (!model) {
      console.warn('Model không được xác định, sử dụng model mặc định:', DEFAULT_MODEL);
      model = DEFAULT_MODEL;
    }
    
    // Kiểm tra trong danh sách model của OpenRouter
    console.log(`Đang kiểm tra model: ${model}`);
    
    // Tạo message để gửi đến API
    let systemMessage = options.system_message || "";
    
    // Tạo cấu trúc request data
    const requestData = {
      model: model,
      messages: [
        {
          role: "system",
          content: systemMessage
        },
        {
          role: "user",
          content: prompt
        }
      ],
      temperature: options.temperature || 0.7,
      max_tokens: options.max_tokens || 500,
      stream: options.stream === true
    };
    
    // Nếu có conversation history, thêm vào messages
    if (options.conversation && Array.isArray(options.conversation) && options.conversation.length > 0) {
      requestData.messages = [
        requestData.messages[0], // Giữ lại system message
        ...options.conversation, // Thêm lịch sử hội thoại
        requestData.messages[requestData.messages.length - 1] // Thêm prompt hiện tại
      ];
    }
    
    // Tạo bản sao của options mà không chứa thông tin nhạy cảm để log an toàn
    const safeOptions = { ...options };
    
    // Xóa các trường nhạy cảm khỏi safeOptions
    delete safeOptions.system_message;
    delete safeOptions.api_key;
    delete safeOptions.model;
    delete safeOptions.conversation;
    
    // Đảm bảo expert_id luôn có giá trị mặc định là "general"
    const expert_id = options.expert_id || "general";
    
    // Thêm context về sơ đồ vào system prompt nếu có
    if (options.diagram_data) {
      let diagramContext = "";
      
      // Kiểm tra xem là chat chung cho toàn sơ đồ hay chat riêng cho node
      const isGeneralDiagramChat = options.service_type === 'diagram_general_chat';
      
      if (isGeneralDiagramChat) {
        // Xây dựng context cho chat về toàn bộ sơ đồ
        diagramContext = `
Đây là dữ liệu về sơ đồ mà người dùng đang tương tác:
- Tên sơ đồ: ${options.diagram_data.title || "Không có"}
- Giới thiệu: ${options.diagram_data.introduction || "Không có"}

Sơ đồ này có các node chính:
${options.diagram_data.nodeChildren && options.diagram_data.nodeChildren.length > 0 
  ? options.diagram_data.nodeChildren.map(child => `- ${child.name}: ${child.description || "Không có mô tả"}`).join('\n')
  : "Không có node con"}

Hãy sử dụng thông tin trên để trả lời câu hỏi của người dùng trong ngữ cảnh của toàn bộ sơ đồ này.
`;
      } else {
        // Xây dựng context cho chat về node cụ thể
        diagramContext = `
Đây là dữ liệu về sơ đồ mà người dùng đang tương tác:
- Tên sơ đồ: ${options.diagram_data.title || "Không có"}
- Giới thiệu: ${options.diagram_data.introduction || "Không có"}
- Node hiện tại: ${options.diagram_data.nodeName || "Không có"}
- Loại node: ${options.diagram_data.nodeType || "Không có"}
- Nội dung node: ${options.diagram_data.nodeContent || "Không có nội dung chi tiết"}

${options.diagram_data.nodeChildren && options.diagram_data.nodeChildren.length > 0 
  ? `Node này có các node con:
${options.diagram_data.nodeChildren.map(child => `- ${child.name}: ${child.description || "Không có mô tả"}`).join('\n')}`
  : "Node này không có node con."}

${options.diagram_data.relatedConcepts && options.diagram_data.relatedConcepts.length > 0
  ? `Các khái niệm liên quan: ${options.diagram_data.relatedConcepts.join(", ")}`
  : ""}

${options.diagram_data.pathToNode && options.diagram_data.pathToNode.length > 0
  ? `Đường dẫn đến node này: ${options.diagram_data.pathToNode.join(" > ")}`
  : ""}

Hãy sử dụng thông tin trên để trả lời câu hỏi về node "${options.diagram_data.nodeName}" này. Nếu người dùng hỏi về node khác hoặc nội dung không có trong node này, hãy giải thích rằng bạn chỉ có thông tin về node hiện tại.
`;
      }
      
      // Thêm thông tin diagram vào system prompt
      systemMessage = systemMessage + "\n" + diagramContext;
    }
    
    // Gói dữ liệu cho proxy request
    const proxyRequestData = {
      prompt,
      // Nội dung bài học hoặc thông tin context sẽ được gửi trong data_info
      data_info: options.content_info || '',
      // Luôn gửi expert_id, đã đảm bảo có giá trị mặc định
      expert_id: expert_id,
      // Tên bài học để thay thế trong system prompt
      lesson_name: options.lessonName,
      // ID khóa học để server có thể lấy thêm nội dung
      course_id: options.course_id,
      // Chat history - tối đa 10 tin nhắn gần nhất
      chat_history: options.chat_history || [],
      // Thêm thông tin trừ credits
      deduct_credits: options.deduct_credits !== false, // Mặc định là true nếu không có
      credit_amount: options.credit_amount || 1, // Mặc định trừ 1 credit
      user_id: userId,
      // Thêm thông tin model để proxy server có thể sử dụng
      model: model,
      // Thêm thông tin system message
      system_message: systemMessage,
      // Thêm thông tin service type
      service_type: options.service_type || 'chat',
      // Truyền diagram_data nếu có
      diagram_data: options.diagram_data || null,
      // Thông tin bổ sung về loại chat diagram
      is_diagram_general_chat: options.service_type === 'diagram_general_chat',
    };
    
    // Xử lý content_info nếu là chuỗi JSON
    if (options.content_info && typeof options.content_info === 'string') {
      try {
        // Kiểm tra xem có phải JSON string hay không
        const parsed = JSON.parse(options.content_info);
        // Nếu parse thành công, coi như đã là object và gán lại
        proxyRequestData.data_info = options.content_info;
        console.log('Đã xử lý content_info dạng JSON string');
      } catch (e) {
        // Nếu không phải JSON, giữ nguyên string
        console.log('Content_info không phải JSON string, giữ nguyên');
        proxyRequestData.data_info = options.content_info;
      }
    }
    
    // Log chi tiết hơn về request khi có lỗi gần đây
    console.log('Đang gửi request qua proxy...', { 
      prompt_length: prompt.length,
      prompt_first_20: prompt.substring(0, 20) + '...',
      service_type: options.service_type || 'chat',
      has_expert_id: true, // Luôn có expert_id (mặc định hoặc được chỉ định)
      expert_id: expert_id, // Hiển thị expert_id đã được đảm bảo có giá trị
      has_content_info: !!options.content_info,
      content_info_length: options.content_info?.length || 0,
      has_lesson_name: !!options.lessonName,
      lesson_name: options.lessonName || 'không có',
      has_course_id: !!options.course_id,
      course_id: options.course_id || 'không có',
      chat_history_count: options.chat_history?.length || 0,
      deduct_credits: options.deduct_credits !== false,
      model: model,
      has_diagram_data: !!options.diagram_data,
      diagram_node_name: options.diagram_data?.nodeName || 'không có',
    });
    
    // Luôn gửi request thông qua proxy server với timeout dài hơn
    try {
      const response = await axios.post(PROXY_URL, proxyRequestData, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 180000 // Tăng timeout lên 3 phút để xử lý system message dài
      });
      
      // Xử lý phản hồi từ proxy
      if (response.data && response.data.answer) {
        // Lưu lại lịch sử sử dụng AI nếu cần - loại bỏ thông tin nhạy cảm
        if (options.track_usage !== false) {
          await trackAIUsage({
            user_id: userId,
            prompt_length: prompt.length,
            response_length: response.data.answer.length,
            tokens_used: response.data.tokens || 0,
            service_type: options.service_type || 'chat',
          });
        }
        
        console.log('Nhận được response từ proxy:', {
          answer_length: response.data.answer.length,
          tokens: response.data.tokens,
          answer_preview: response.data.answer.substring(0, 100) + '...'
        });
        
        return response.data.answer;
      }
      
      console.error('Phản hồi từ proxy không hợp lệ:', response.data);
      throw new Error('Không nhận được kết quả từ AI');
    } catch (axiosError) {
      console.error('Chi tiết lỗi axios:', {
        message: axiosError.message,
        code: axiosError.code,
        hasResponse: !!axiosError.response,
        responseStatus: axiosError.response?.status,
        responseData: axiosError.response?.data
      });
      
      // Xử lý lỗi timeout và kết nối đặc biệt
      if (axiosError.code === 'ECONNABORTED') {
        console.error('Lỗi timeout khi gọi proxy server:', axiosError.message);
        throw new Error('Yêu cầu AI đã vượt quá thời gian chờ. Vui lòng thử lại với prompt ngắn hơn.');
      }
      
      // Xử lý lỗi 404 khi API trừ credit không tồn tại
      if (axiosError.response && axiosError.response.status === 404 && 
          axiosError.response.config && axiosError.response.config.url && 
          axiosError.response.config.url.includes('deduct-for-ai')) {
        console.warn('API trừ credit không tồn tại, bỏ qua trừ credit');
        // Tiếp tục xử lý mà không trừ credit
      }
      
      throw axiosError; // Ném lại lỗi để xử lý ở bên ngoài
    }
  } catch (error) {
    console.error('Lỗi khi gọi OpenRouter API qua proxy:', error);
    
    // Nếu lỗi là do model không khả dụng và có backup models, thử model khác
    if (error.response?.status === 404 || error.response?.status === 429 || 
        (error.response?.data?.error && error.response?.data?.error.includes('model'))) {
      // Xóa phần ngày tháng từ ID model nếu có
      const currentModel = normalizeModelId(options.model);
      const currentModelIndex = BACKUP_MODELS.indexOf(currentModel);
      const nextModelIndex = currentModelIndex === -1 ? 0 : (currentModelIndex + 1) % BACKUP_MODELS.length;
      const backupModel = BACKUP_MODELS[nextModelIndex];
      
      console.log(`Thử lại với model backup: ${backupModel}`);
      
      return handleOpenRouterRequest(prompt, {
        ...options,
        model: backupModel
      });
    }
    
    // Tùy chỉnh thông báo lỗi dựa trên loại lỗi
    let errorMessage = 'Không thể kết nối đến dịch vụ AI. Vui lòng kiểm tra kết nối mạng và thử lại sau.';
    
    if (error.message && error.message.includes('timeout')) {
      errorMessage = 'Yêu cầu AI đã vượt quá thời gian chờ. Vui lòng thử lại với prompt ngắn hơn.';
    } else if (error.response && error.response.status) {
      errorMessage += ` Lỗi HTTP: ${error.response.status}.`;
    }
    
    throw new Error(errorMessage + ' Nếu lỗi vẫn tiếp tục, hãy liên hệ với quản trị viên.');
  }
}

/**
 * Tạo khóa học với AI
 * @param {string} prompt - Mô tả khóa học
 * @param {Object} courseBasics - Thông tin cơ bản của khóa học
 * @param {Object} additionalData - Dữ liệu bổ sung
 * @param {Object} options - Tùy chọn bổ sung, bao gồm model
 * @returns {Promise<Object>} - Dữ liệu khóa học được tạo
 */
export async function generateCourseWithOpenRouter(prompt, courseBasics = {}, additionalData = {}, options = {}) {
  try {
    // Tạo prompt cho AI
    const aiPrompt = `
Tạo một khóa học đầy đủ với mô tả sau: ${prompt}

${additionalData.category ? `Danh mục: ${additionalData.category}` : ''}
${additionalData.difficulty ? `Độ khó: ${additionalData.difficulty}` : ''}
${additionalData.lessonCount ? `Số bài học: ${additionalData.lessonCount}` : ''}

Hãy tạo một khóa học hoàn chỉnh với:
1. Tiêu đề khóa học rõ ràng
2. Mô tả tổng quan về khóa học
3. Danh sách các bài học (nodes) với:
   - Tiêu đề bài học
   - Mô tả ngắn gọn
   - Nội dung chi tiết
   - Các tài nguyên bổ sung (nếu có)
4. Đề xuất thời gian ước tính để hoàn thành khóa học

Trả về kết quả dưới dạng JSON với cấu trúc sau:
{
  "id": "${courseBasics.id || 'course-' + Date.now()}",
  "name": "Tên khóa học",
  "category": "ID danh mục (để trống nếu không chỉ định)",
  "difficulty": "beginner/intermediate/advanced",
  "description": "Mô tả chi tiết về khóa học",
  "tags": ["tag1", "tag2"],
  "estimated_time": "Thời gian ước tính để hoàn thành khóa học (VD: 2 tuần với 30 phút/ngày)",
  "startnodeid": "ID của node đầu tiên",
  "nodes": [
    {
      "id": "node-1",
      "name": "Tên bài học",
      "description": "Mô tả ngắn về bài học",
      "content": "Nội dung chi tiết của bài học",
      "type": "lesson/practice/quiz",
      "children": ["node-2"],
      "resources": [
        {
          "type": "video/article/document",
          "title": "Tiêu đề tài nguyên",
          "url": "URL tài nguyên",
          "description": "Mô tả tài nguyên"
        }
      ]
    }
  ]
}
`;
    
    // Lấy system_message từ options
    const systemMessage = options.system_message || '';
    if (!systemMessage) {
      console.warn('Warning: No system_message provided to generateCourseWithOpenRouter. This may result in generic content generation.');
    }
    
    // Kết hợp tùy chọn mặc định với tùy chọn được cung cấp, không override system_message
    const requestOptions = {
      max_tokens: 4000,
      temperature: 0.7,
      service_type: 'course_generation',
      ...options,
      system_message: systemMessage // Đảm bảo không bị override bởi options.system_message
    };
    
    // Gọi AI để tạo khóa học
    const aiResponse = await handleOpenRouterRequest(aiPrompt, requestOptions);
    
    // Xử lý kết quả từ AI
    try {
      // Tìm và trích xuất phần JSON từ phản hồi
      const jsonMatch = aiResponse.match(/```json\n([\s\S]*?)\n```/) || 
                      aiResponse.match(/```\n([\s\S]*?)\n```/) || 
                      aiResponse.match(/\{[\s\S]*\}/);
      
      let jsonStr = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : aiResponse;
      
      // Nếu không có dấu hiệu JSON, coi toàn bộ phản hồi là JSON
      if (!jsonStr.includes('{')) {
        jsonStr = aiResponse;
      }
      
      // Đảm bảo jsonStr chỉ chứa một JSON object hợp lệ
      jsonStr = jsonStr.trim();
      if (!jsonStr.startsWith('{')) {
        jsonStr = jsonStr.substring(jsonStr.indexOf('{'));
      }
      if (!jsonStr.endsWith('}')) {
        jsonStr = jsonStr.substring(0, jsonStr.lastIndexOf('}') + 1);
      }
      
      // Parse JSON
      const courseData = JSON.parse(jsonStr);
      
      // Đảm bảo ID cho tất cả các node
      if (courseData.nodes && Array.isArray(courseData.nodes)) {
        courseData.nodes.forEach((node, index) => {
          if (!node.id) {
            node.id = `node-${index + 1}`;
          }
        });
        
        // Đảm bảo startnodeid trỏ đến node đầu tiên
        if (!courseData.startnodeid && courseData.nodes.length > 0) {
          courseData.startnodeid = courseData.nodes[0].id;
        }
      }
      
      return courseData;
    } catch (error) {
      console.error('Lỗi khi xử lý phản hồi từ AI:', error);
      console.log('AI response:', aiResponse);
      throw new Error('Không thể xử lý phản hồi từ AI');
    }
  } catch (error) {
    console.error('Lỗi khi tạo khóa học với AI:', error);
    throw error;
  }
}

/**
 * Gửi tin nhắn chat đến AI
 * @param {string} message - Nội dung tin nhắn
 * @param {Object} options - Các tùy chọn bổ sung
 * @param {string} conversationId - ID cuộc hội thoại
 * @returns {Promise<Object>} - Phản hồi từ AI
 */
export async function chatWithOpenRouter(
  message,
  options = {},
  conversationId = null
) {
  try {
    if (DEBUG_MODE || true) { // Tạm thời bật debug cho mọi trường hợp
      console.log("OpenRouter chatWithOpenRouter input:", {
        message: message,
        messageLength: message?.length || 0,
        hasExpertId: !!options.expert_id,
        expertId: options.expert_id || "general",
        hasContentInfo: !!options.content_info,
        contentInfoLength: options.content_info?.length || 0,
        lessonName: options.lessonName || "không có",
        courseId: options.course_id || "không có",
        hasChatHistory:
          !!options.chat_history && options.chat_history.length > 0,
        chatHistoryCount: options.chat_history?.length || 0,
        hasDiagramData: !!options.diagram_data,
        diagramTitle: options.diagram_data?.title || "không có",
        diagramNodeName: options.diagram_data?.nodeName || "không có",
        conversationId,
      });
    }

    // Kiểm tra message có giá trị không
    if (!message) {
      console.error("Lỗi: message là undefined hoặc rỗng trong chatWithOpenRouter");
      return {
        message: "Vui lòng nhập nội dung tin nhắn",
        error: true,
      };
    }

    // Lấy modelId hiện tại
    const modelId = await getModelIdFromStorage();
    console.log(`Sử dụng model: ${modelId}`);

    // Đảm bảo expert_id luôn có giá trị, mặc định là "general"
    options.expert_id = options.expert_id || "general";
    
    // Lấy system prompt dựa trên expert_id
    let systemPrompt = await getSystemPrompt(options.expert_id, options.lessonName);

    // Thêm context về sơ đồ vào system prompt nếu có
    if (options.diagram_data) {
      let diagramContext = "";
      
      // Kiểm tra xem là chat chung cho toàn sơ đồ hay chat riêng cho node
      const isGeneralDiagramChat = options.service_type === 'diagram_general_chat';
      
      if (isGeneralDiagramChat) {
        // Xây dựng context cho chat về toàn bộ sơ đồ
        diagramContext = `
Đây là dữ liệu về sơ đồ mà người dùng đang tương tác:
- Tên sơ đồ: ${options.diagram_data.title || "Không có"}
- Giới thiệu: ${options.diagram_data.introduction || "Không có"}

Sơ đồ này có các node chính:
${options.diagram_data.nodeChildren && options.diagram_data.nodeChildren.length > 0 
  ? options.diagram_data.nodeChildren.map(child => `- ${child.name}: ${child.description || "Không có mô tả"}`).join('\n')
  : "Không có node con"}

Hãy sử dụng thông tin trên để trả lời câu hỏi của người dùng trong ngữ cảnh của toàn bộ sơ đồ này.
`;
      } else {
        // Xây dựng context cho chat về node cụ thể
        diagramContext = `
Đây là dữ liệu về sơ đồ mà người dùng đang tương tác:
- Tên sơ đồ: ${options.diagram_data.title || "Không có"}
- Giới thiệu: ${options.diagram_data.introduction || "Không có"}
- Node hiện tại: ${options.diagram_data.nodeName || "Không có"}
- Loại node: ${options.diagram_data.nodeType || "Không có"}
- Nội dung node: ${options.diagram_data.nodeContent || "Không có nội dung chi tiết"}

${options.diagram_data.nodeChildren && options.diagram_data.nodeChildren.length > 0 
  ? `Node này có các node con:
${options.diagram_data.nodeChildren.map(child => `- ${child.name}: ${child.description || "Không có mô tả"}`).join('\n')}`
  : "Node này không có node con."}

${options.diagram_data.relatedConcepts && options.diagram_data.relatedConcepts.length > 0
  ? `Các khái niệm liên quan: ${options.diagram_data.relatedConcepts.join(", ")}`
  : ""}

${options.diagram_data.pathToNode && options.diagram_data.pathToNode.length > 0
  ? `Đường dẫn đến node này: ${options.diagram_data.pathToNode.join(" > ")}`
  : ""}

Hãy sử dụng thông tin trên để trả lời câu hỏi về node "${options.diagram_data.nodeName}" này. Nếu người dùng hỏi về node khác hoặc nội dung không có trong node này, hãy giải thích rằng bạn chỉ có thông tin về node hiện tại.
`;
      }
      
      // Thêm thông tin diagram vào system prompt
      systemPrompt = systemPrompt + "\n" + diagramContext;
    }
    
    // Gọi AI để chat
    const aiResponse = await handleOpenRouterRequest(message, {
      ...options,
      system_message: systemPrompt,
      model: modelId,
      track_usage: true,
      service_type: 'chat',
      expert_id: options.expert_id,
      lessonName: options.lessonName,
      course_id: options.course_id,
      chat_history: options.chat_history || [],
      deduct_credits: options.deduct_credits !== false,
      credit_amount: options.credit_amount || 1,
      user_id: options.user_id || getUserId(),
      diagram_data: options.diagram_data || null,
      is_diagram_general_chat: options.service_type === 'diagram_general_chat',
    });
    
    // Lưu lịch sử chat
    if (conversationId) {
      await saveChatHistoryToStorage(conversationId, {
        role: 'user',
        content: message
      }, {
        role: 'assistant',
        content: aiResponse
      });
    }
    
    return {
      message: aiResponse,
      error: false
    };
  } catch (error) {
    console.error('Lỗi khi chat với AI:', error);
    return {
      message: 'Không thể kết nối đến dịch vụ AI. Vui lòng kiểm tra kết nối mạng và thử lại sau.',
      error: true
    };
  }
}

/**
 * Theo dõi việc sử dụng AI và cập nhật credits của người dùng
 * @param {Object} usageData - Dữ liệu sử dụng AI
 */
export async function trackAIUsage(usageData = {}) {
  try {
    if (!usageData.user_id) {
      console.warn('Không có user_id khi theo dõi sử dụng AI');
      return;
    }
    
    // Loại bỏ thông tin nhạy cảm khỏi log
    console.log('Thông tin sử dụng AI:', {
      prompt_length: usageData.prompt_length || 0,
      response_length: usageData.response_length || 0,
      tokens_used: usageData.tokens_used || 0,
      service_type: usageData.service_type || 'chat',
      timestamp: new Date().toISOString()
    });
    
    // Lưu vào localStorage nếu đang ở môi trường browser
    if (typeof window !== 'undefined') {
      try {
        const aiUsage = JSON.parse(localStorage.getItem('ai_usage') || '[]');
        aiUsage.push({
          user_id: usageData.user_id,
          prompt_length: usageData.prompt_length || 0,
          response_length: usageData.response_length || 0,
          tokens_used: usageData.tokens_used || 0,
          service_type: usageData.service_type || 'chat',
          timestamp: new Date().toISOString()
        });
        
        // Giới hạn lịch sử sử dụng để không quá lớn
        if (aiUsage.length > 100) {
          aiUsage.splice(0, aiUsage.length - 100);
        }
        
        localStorage.setItem('ai_usage', JSON.stringify(aiUsage));
      } catch (error) {
        console.error('Lỗi khi lưu thông tin sử dụng AI vào localStorage:', error);
      }
    }
  } catch (error) {
    console.error('Lỗi khi theo dõi sử dụng AI:', error);
  }
}

export default {
  handleOpenRouterRequest,
  generateCourseWithOpenRouter,
  chatWithOpenRouter,
  trackAIUsage
};